/**
 * 全平台模块开关消费（超管理台「模块管理」定义；生态门户导航按开关渲染）。
 *
 * Provider 挂载时从链端拉取 `getAppConfig`（modules + content），
 * 失败时全开（不影响可用性）。UI 层通过 `useModules().isOn(id)` 判定。
 */

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ModuleConfig, ModuleId } from '@shared/chain';
import { container } from '@/services/container';

const ALL_ON: ModuleConfig = {
  chat: true,
  feed: true,
  trade: true,
  rewards: true,
  discover: true,
  search: true,
  checkin: true,
  recommend: true,
  onboarding: true,
  merchant: true,
};

export interface ModulesApi {
  modules: ModuleConfig;
  /** 模块是否开启（未知/加载中按开启处理，避免导航闪没）。 */
  isOn(id: ModuleId): boolean;
}

const ModulesContext = createContext<ModulesApi>({ modules: ALL_ON, isOn: () => true });

export function ModulesProvider({ children }: { children: ReactNode }): JSX.Element {
  const [modules, setModules] = useState<ModuleConfig>(ALL_ON);
  useEffect(() => {
    let alive = true;
    container.chain
      .getAppConfig()
      .then((cfg) => {
        if (alive && cfg?.modules) setModules({ ...ALL_ON, ...cfg.modules });
      })
      .catch(() => {
        // 拉取失败保持全开
      });
    return () => {
      alive = false;
    };
  }, []);
  const api = useMemo<ModulesApi>(() => ({ modules, isOn: (id) => modules[id] !== false }), [modules]);
  return <ModulesContext.Provider value={api}>{children}</ModulesContext.Provider>;
}

export function useModules(): ModulesApi {
  return useContext(ModulesContext);
}

export default ModulesProvider;
